import {
  createFileRoute,
  useNavigate,
  useRouteContext,
  useSearch,
} from "@tanstack/react-router";
import type { JSX } from "react";

import CheckCircle from "@repo/icons/default/CheckCircle";
import Clock from "@repo/icons/default/Clock";
import Doctor from "@repo/icons/default/Doctor";
import Laptop from "@repo/icons/default/Laptop";
import Left from "@repo/icons/default/Left";
import Link1 from "@repo/icons/default/Link1";
import Phone from "@repo/icons/default/Phone";
import Settings from "@repo/icons/default/Settings";
import Smile from "@repo/icons/default/Smile";
import UserRefresh from "@repo/icons/default/UserRefresh";
import XRay from "@repo/icons/default/XRay";
import { CpsBadge } from "corpus";

import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { MenuList, type MenuListItem } from "@/components/ui/layout/menu/menu-list";
import { Page } from "@/components/ui/layout/page/page";
import { ServicePackageEnum } from "@/lib/enums/service-package-enum";
import { AssistProBadge } from "@/routes/settings/assist/-components/assist-pro-badge.tsx";
import { AssistProMenuBanner } from "@/routes/settings/assist/-components/assist-pro-menu-banner.tsx";
import { TasksMenuTag } from "@/routes/settings/assist/-components/tasks-menu-tag";

const LivanceAssistMenuPage = (): JSX.Element => {
  const { flags, user } = useRouteContext({ from: "__root__" });
  const navigate = useNavigate();
  const params: { from: "/home" | undefined } = useSearch({ strict: false });

  const shouldShowBanner = flags["exibe-banner-assist-no-menu"];
  const shouldShowSiteOverviewItem =
    flags["habilita-item-de-relatorio-do-site-no-menu-do-assist"];

  const isAssistProUser = user.pacotesServicos.find(
    (servicePkg) => servicePkg.codPacoteServicos === ServicePackageEnum.Pro.valueOf(),
  );

  const isEssentialUser: boolean = user.pacotesServicos.some(
    (servicePkg) =>
      servicePkg.codPacoteServicos === ServicePackageEnum.Essential.valueOf(),
  );

  const isUserInTrialPeriod = isAssistProUser?.trialAtivo ?? false;

  const showFeatureSettingsButton =
    flags["exibe-configuracoes-de-funcionalidades-assist"];

  const showContactHistoryItem =
    flags["habilita-historico-de-contatos-no-menu-do-assist"] &&
    !isUserInTrialPeriod &&
    !isEssentialUser;

  const showContactHistoryTrialItem =
    flags["habilita-historico-de-contatos-no-menu-do-assist-trial"] &&
    isUserInTrialPeriod;

  const showContactHistoryEssentialItem =
    flags["habilita-historico-de-contatos-no-menu-do-assist-essencial"] &&
    isEssentialUser;

  const showTasksItem =
    flags["exibir-tela-de-tarefas-assist"] && Boolean(isAssistProUser);

  const menuItens: (MenuListItem & { show?: boolean })[] = [
    {
      name: "Tarefas",
      Icon: CheckCircle,
      route: "/settings/assist/tasks",
      tag: <TasksMenuTag />,
      description: "Veja as demandas de pacientes que precisam de você.",
      show: showTasksItem,
    },
    {
      name: "Raio-X de contatos",
      Icon: XRay,
      route: "/settings/assist/contacts-history",
      tag: <CpsBadge color="helper">Novo</CpsBadge>,
      description: "Veja os detalhes de cada contato que atendemos para você.",
      show: showContactHistoryItem,
    },
    {
      name: "Raio-X de contatos - Trial",
      Icon: XRay,
      route: "/settings/assist/contacts-history/trial-period",
      tag: <CpsBadge color="helper">Novo</CpsBadge>,
      description: "Veja os detalhes de cada contato que atendemos para você.",
      show: showContactHistoryTrialItem,
    },
    {
      name: "Raio-X de contatos - Essencial",
      Icon: XRay,
      route: "/settings/assist/contacts-history/essential",
      tag: <CpsBadge color="helper">Novo</CpsBadge>,
      description: "Veja os contatos que querem falar com você.",
      show: showContactHistoryEssentialItem,
    },
    {
      name: "Relatório de contatos",
      Icon: Phone,
      route: "/settings/assist/customer-contact-overview",
      tag: <AssistProBadge />,
      description: "Acompanhe o progresso dos contatos que buscam seu consultório.",
    },
    {
      name: "Relatório de consultas",
      Icon: Doctor,
      route: "/settings/assist/customer-appointment-overview",
      description: "Monitore o fluxo de consultas agendadas e realizadas.",
    },
    {
      name: "Relatório do site",
      Icon: Laptop,
      route: "/settings/assist/customer-site-overview",
      description: "Acompanhe o alcance e a receita gerada pelo seu site Livance.",
      show: shouldShowSiteOverviewItem,
    },
    {
      name: "Links com rastreamento",
      Icon: Link1,
      route: "/settings/assist/user-trackable-links",
      description:
        "Crie links com rastreamento para monitorar os acessos e medir a performance das suas divulgações.",
    },
    {
      name: "Lembretes programados",
      Icon: Clock,
      route: "/settings/assist/follow-up-appointment-reminder",
      description: "Veja os pacientes com lembretes de retorno programados por você.",
      tag: <AssistProBadge />,
    },
    {
      name: "Personalizar atendimento",
      Icon: Smile,
      route: "/settings/assist/secretary-details",
      description:
        "Forneça informações para as secretárias atenderem de forma personalizada e agendarem mais.",
      tag: <AssistProBadge />,
    },
    {
      name: "Recuperação de agendamentos",
      Icon: UserRefresh,
      route: "/settings/assist/rescued-appointment-overview",
      description:
        "Automatize mensagens para recuperar consultas canceladas ou perdidas de forma prática.",
      tag: <AssistProBadge />,
    },
  ];

  const activeMenuItens = menuItens.filter((item) => item.show !== false);

  const onSettingsButtonClick = (): void => {
    void navigate({
      to: "/settings/assist/feature-settings",
    });
  };

  const navigateBack = (): void => {
    if (params.from === "/home") {
      void navigate({ to: "/home" });
    } else {
      void navigate({ to: ".." });
    }
  };

  return (
    <>
      {showFeatureSettingsButton && isAssistProUser ? (
        <HeaderRoot>
          <HeaderButton icon={Left} onClick={navigateBack} align="start" />
          <HeaderTitle title="Livance Assist" />
          <HeaderButton
            icon={Settings}
            align="end"
            onClick={onSettingsButtonClick}
            data-testid="header-settings-button"
          />
        </HeaderRoot>
      ) : (
        <HeaderRoot>
          <HeaderButton icon={Left} onClick={navigateBack} align="start" />
          <HeaderTitle title="Livance Assist" />
        </HeaderRoot>
      )}
      <Page className="pt-2">
        {shouldShowBanner ? (
          <div className="pt-4 pb-2">
            <AssistProMenuBanner />
          </div>
        ) : null}
        <MenuList menuItems={activeMenuItens} />
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/assist/")({
  component: LivanceAssistMenuPage,
});
